/**
 * ActiveSessionPanel — the "session active" surface: token validity + two actions.
 *
 *  1. Token refresh — POST /auth/refresh rotation via the api layer's refreshSession() (single
 *     in-flight, rotates the family), then re-pull identity/caps. Display-only token metadata; the
 *     token itself never leaves the in-memory tokenStore.
 *  2. Session-kill — POST /auth/logout (the server revokes the refresh family), behind an explicit
 *     confirm Modal (irreversible for the active tab). Routes back to /login via the store purge.
 *
 * Neither action carries a capability gate: any authenticated user may end / refresh their OWN
 * session (server-authoritative; no cap key in server/src/modules/iam for self-logout).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, Modal, useToast } from "@/ui";
import { ApiError, refreshSession } from "@/api";
import { useAuthStore } from "@/auth";
import { useT, useUiStore } from "@/app";
import type { SessionView } from "../useSession";
import { useRefreshSession } from "../useSession";

export interface ActiveSessionPanelProps {
  session: SessionView;
}

function formatExpiry(epochMs: number | null, locale: string): string {
  if (epochMs == null) return "—";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar" : "fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(epochMs));
}

export function ActiveSessionPanel({ session }: ActiveSessionPanelProps) {
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const locale = useUiStore((s) => s.locale);
  const online = useUiStore((s) => s.online);
  const logout = useAuthStore((s) => s.logout);
  const refreshCaps = useRefreshSession();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [killing, setKilling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const tokenTone = !session.tokenPresent ? "neutral" : session.tokenExpiringSoon ? "warn" : "success";
  const tokenLabel = !session.tokenPresent
    ? t("auth.session.token.absent")
    : session.tokenExpiringSoon
      ? t("auth.session.token.expiring")
      : t("auth.session.token.valid");

  const onRefreshToken = async () => {
    setRefreshing(true);
    try {
      await refreshSession();
      await refreshCaps();
      toast.success(t("auth.session.token.refreshed"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("auth.session.token.refresh.error"));
    } finally {
      setRefreshing(false);
    }
  };

  const onConfirmKill = async () => {
    setKilling(true);
    try {
      await logout();
      toast.success(t("auth.session.kill.done"));
      navigate("/login", { replace: true });
    } catch (err) {
      // logout() is best-effort and purges locally even on transport failure; still route out.
      toast.error(err instanceof ApiError ? err.message : t("auth.session.kill.error"));
      navigate("/login", { replace: true });
    } finally {
      setKilling(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Card title={t("auth.session.tokens")}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "max-content 1fr",
            gap: "var(--nv-s-3) var(--nv-s-5)",
            margin: 0,
            alignItems: "center",
          }}
        >
          <dt style={{ color: "var(--nv-slate)" }}>{t("auth.session.token.access")}</dt>
          <dd style={{ margin: 0 }}>
            <Badge tone={tokenTone}>{tokenLabel}</Badge>
          </dd>
          <dt style={{ color: "var(--nv-slate)" }}>{t("auth.session.token.expires")}</dt>
          <dd className="nv-mono nv-ltr-island" style={{ margin: 0 }}>
            {formatExpiry(session.tokenExpiresAt, locale)}
          </dd>
        </dl>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--nv-s-3)" }}>
          <Button
            variant="secondary"
            loading={refreshing}
            disabled={!session.tokenPresent || !online}
            onClick={onRefreshToken}
          >
            {t("auth.session.token.refresh")}
          </Button>
          <Button variant="danger" onClick={() => setConfirmOpen(true)}>
            {t("auth.session.kill.action")}
          </Button>
        </div>
        <p style={{ margin: 0, color: "var(--nv-slate)", fontSize: "var(--nv-fs-label)" }}>
          {t("auth.session.kill.desc")}
        </p>
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t("auth.session.kill.confirm.title")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={killing}>
              {t("auth.session.kill.cancel")}
            </Button>
            <Button variant="danger" loading={killing} onClick={onConfirmKill}>
              {t("auth.session.kill.confirm.ok")}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>{t("auth.session.kill.confirm.body")}</p>
      </Modal>
    </Card>
  );
}
